import { useRef, useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";

export default function BeforeAfter({ before, after, alt = "Transformação", testid }) {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);

  const onMove = useCallback(
    (clientX) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const p = ((clientX - rect.left) / rect.width) * 100;
      setPos(Math.max(0, Math.min(100, p)));
    },
    []
  );

  useEffect(() => {
    if (!dragging) return;
    const mm = (e) => onMove(e.clientX ?? e.touches?.[0]?.clientX ?? 0);
    const up = () => setDragging(false);
    window.addEventListener("mousemove", mm);
    window.addEventListener("touchmove", mm, { passive: false });
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", mm);
      window.removeEventListener("touchmove", mm);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, [dragging, onMove]);

  return (
    <motion.div
      ref={containerRef}
      data-testid={testid}
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6 }}
      className="relative w-full aspect-[3/4] sm:aspect-[4/5] overflow-hidden rounded-3xl border border-white/10 bg-neutral-900 select-none group"
      onMouseDown={(e) => {
        setDragging(true);
        onMove(e.clientX);
      }}
      onTouchStart={(e) => {
        setDragging(true);
        onMove(e.touches[0].clientX);
      }}
    >
      <img
        src={after}
        alt={`${alt} — depois`}
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
      />
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <img
          src={before}
          alt={`${alt} — antes`}
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
      </div>

      <span className="absolute top-4 left-4 rounded-full bg-black/70 backdrop-blur border border-white/15 text-white text-[10px] sm:text-xs font-bold uppercase tracking-widest px-3 py-1">
        Antes
      </span>
      <span className="absolute top-4 right-4 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-[10px] sm:text-xs font-bold uppercase tracking-widest px-3 py-1">
        Depois
      </span>

      <div
        className="absolute top-0 bottom-0 w-[2px] bg-gradient-to-b from-yellow-400 to-orange-500 pointer-events-none"
        style={{ left: `${pos}%` }}
      >
        <div className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black border-2 border-yellow-400 shadow-[0_0_24px_rgba(250,204,21,0.5)] flex items-center justify-center">
          <svg viewBox="0 0 24 24" className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l-7 7 7 7M15 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </motion.div>
  );
}
